/**
 * Mobile PWA Installation Detection and Management
 */

declare global {
  interface BeforeInstallPromptEvent extends Event {
    prompt(): void;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  }
}

import { getDeviceInfo } from './deviceDetection';
import logger from './logger';

export const isMobileDevice = (): boolean => {
    return getDeviceInfo().isMobile;
};

export const isIOS = (): boolean => {
    return getDeviceInfo().isIOS;
};

export const isPWAInstallable = (): boolean => {
  // Check if app is already installed
  if (window.matchMedia('(display-mode: standalone)').matches) {
    logger.log('App is already installed in standalone mode');
    return false;
  }

  const device = getDeviceInfo();
  // No iOS browser supports beforeinstallprompt (Safari, Chrome, Firefox, Edge, etc.)
  // Also show custom UI for Safari on macOS
  // Android/other platforms use beforeinstallprompt if available
  return device.isIOS || device.isSafari || 'BeforeInstallPromptEvent' in window;
};

export const isInstalledPWA = (): boolean => {
  // iOS home-screen apps report through navigator.standalone; the display-mode
  // media query is only supported on recent versions of Safari, so checking it
  // alone makes an installed iOS PWA look like a normal browser tab.
  if ((window.navigator as any).standalone === true) return true;
  return window.matchMedia('(display-mode: standalone)').matches;
};

export const shouldAutoFullscreen = (): boolean => {
  const device = getDeviceInfo();
  
  // Auto-fullscreen on mobile except Safari (which doesn't support fullscreen API)
  return device.isMobile && !device.isSafari;
};

export const isDismissedInstallPrompt = (): boolean => {
  try {
    const dismissed = localStorage.getItem('installPromptDismissed');
    if (!dismissed) return false;

    const dismissTime = parseInt(dismissed, 10);
    const now = Date.now();
    // Re-show after 7 days
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

    return now - dismissTime < sevenDaysMs;
  } catch {
    return false;
  }
};

export const dismissInstallPrompt = (): void => {
  try {
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  } catch (error) {
    logger.warn('Failed to save install prompt dismissal:', error);
  }
};

export const clearInstallPromptDismissal = (): void => {
  try {
    localStorage.removeItem('installPromptDismissed');
  } catch (error) {
    logger.warn('Failed to clear install prompt dismissal:', error);
  }
};

export const shouldShowInstallPrompt = (): boolean => {
  return false; // isMobileDevice() && isPWAInstallable() && !isDismissedInstallPrompt(); PSB: An installed app can bring problems. For instance, the user is clicking the app link but the installed app is already started...
};

// Return the BeforeInstallPromptEvent if available
export let deferredPrompt: BeforeInstallPromptEvent | null = null;

export const initializeInstallPrompt = (): ((onComplete: () => void) => void) => {
  let onCompleteCallback: (() => void) | null = null;

  if (isMobileDevice()) {
    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      deferredPrompt = event as BeforeInstallPromptEvent;
    });

    window.addEventListener('appinstalled', () => {
      deferredPrompt = null;
      clearInstallPromptDismissal();
      onCompleteCallback?.();
    });
  }

  return (callback: () => void) => {
    onCompleteCallback = callback;
  };
};

export const triggerInstallPrompt = async (): Promise<boolean> => {
  if (!deferredPrompt) return false;

  try {
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      deferredPrompt = null;
      return true;
    }
    return false;
  } catch (error) {
    logger.warn('Error triggering install prompt:', error);
    return false;
  }
};
